import { useState, useEffect } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { prescriptionsService } from '@/services/prescriptions';
import { patientsService } from '@/services/patients';
import { drugsService } from '@/services/drugs';
import { getApiError } from '@/services/api';
import type { CreatePrescriptionInput, PrescriptionItemInput, InteractionWarning } from '@dpfs/shared';
import { formatPackagingHint } from '@dpfs/shared/packaging';
import { AlertCircle, AlertTriangle, Check, FileText, ImageIcon, Plus, Search, ShieldAlert, Trash2, UserPlus, X } from 'lucide-react';
import { PageHeader, Spinner } from '@/components/ui';
import { DrugNoticeModal } from '@/components/DrugNoticeModal';
import { patientIdentifier } from '@/lib/patient';

interface PrescriptionForm extends Omit<CreatePrescriptionInput, 'items'> {
  items: (PrescriptionItemInput & { drugName?: string; controlledClass?: string; noticeUrl?: string; packagingSize?: number; dosageFormRaw?: string; quantityUnit?: string })[];
}

const severityStyles: Record<string, string> = {
  SEVERE: 'bg-danger-50 border-danger-200 text-danger-800',
  MODERATE: 'bg-orange-50 border-orange-200 text-orange-800',
  MILD: 'bg-warning-50 border-warning-200 text-warning-800',
};

// Common administration instructions, inserted verbatim into the (French) prescription text.
const INSTRUCTION_CHIPS = [
  'Avant les repas', 'Pendant les repas', 'Après les repas',
  'Le matin', 'Le midi', 'Le soir', 'Au coucher',
  'À jeun', 'Avec un grand verre d\'eau',
];

const QUANTITY_UNITS = [
  ['unité', 'unit'], ['comprimé', 'tablet'], ['boîte', 'box'], ['flacon', 'bottle'], ['ampoule', 'ampoule'],
  ['sachet', 'sachet'], ['tube', 'tube'], ['suppositoire', 'suppository'], ['patch', 'patch'],
] as const;

export function NewPrescription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPatientId = searchParams.get('patientId');
  const duplicateItemsParam = searchParams.get('items');
  const [error, setError] = useState<string | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [drugSearches, setDrugSearches] = useState<Record<number, string>>({});
  const [activeDrugSearchIndex, setActiveDrugSearchIndex] = useState<number | null>(null);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);
  const [noticeModalDrugId, setNoticeModalDrugId] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PrescriptionForm>({
    defaultValues: {
      patientId: preselectedPatientId || '',
      documentType: 'MEDICATION',
      prescriptionType: 'ACUTE',
      items: [{ drugId: '', dosage: '', frequency: '', duration: '', quantity: 1, quantityUnit: 'unité', substitutionAllowed: true }],
      validityDays: 15,
    },
  });

  const documentType = watch('documentType') || 'MEDICATION';
  const isMedication = documentType === 'MEDICATION';

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Fetch preselected patient data
  const { data: preselectedPatient } = useQuery({
    queryKey: ['patient', preselectedPatientId],
    queryFn: () => patientsService.getById(preselectedPatientId!),
    enabled: !!preselectedPatientId,
  });

  // Set patient search display when preselected patient loads
  useEffect(() => {
    if (preselectedPatient && preselectedPatientId) {
      const idDoc = preselectedPatient.identityType === 'PASSPORT' ? preselectedPatient.passport : preselectedPatient.nin;
      const idDisplay = idDoc ? idDoc.slice(-4) : '';
      setPatientSearch(`${preselectedPatient.lastName} ${preselectedPatient.firstName} (****${idDisplay})`);
      setValue('patientId', preselectedPatientId);
    }
  }, [preselectedPatient, preselectedPatientId, setValue]);

  // Handle duplicate prescription items from URL
  useEffect(() => {
    if (duplicateItemsParam) {
      try {
        const items = JSON.parse(decodeURIComponent(duplicateItemsParam));
        if (Array.isArray(items) && items.length > 0) {
          // Clear existing items and add duplicated ones
          const formItems = items.map((item: any) => ({
            drugId: item.drugId || '',
            quantity: item.quantity || 1,
            quantityUnit: item.quantityUnit || 'unité',
            dosage: item.dosage || '',
            frequency: item.frequency || '',
            duration: item.duration || '',
            instructions: item.instructions || '',
            substitutionAllowed: item.substitutionAllowed ?? true,
            drugName: '', // Will be filled by drug lookup
          }));
          setValue('items', formItems);

          // Fetch drug names for display
          items.forEach(async (item: any, index: number) => {
            if (item.drugId) {
              try {
                const drug = await drugsService.getById(item.drugId);
                if (drug) {
                  setValue(`items.${index}.drugName`, `${drug.tradeName} (${drug.dci}) - ${drug.strength}`);
                  // Restore controlled-substance + packaging info so warnings and hints show for duplicates too
                  setValue(`items.${index}.controlledClass`, drug.controlledClass ?? '');
                  setValue(`items.${index}.noticeUrl`, drug.noticeUrl ?? '');
                  setValue(`items.${index}.packagingSize`, drug.packagingSize ?? 0);
                  setValue(`items.${index}.dosageFormRaw`, drug.dosageForm);
                  if (drug.controlledClass === 'A') {
                    setValue(`items.${index}.substitutionAllowed`, false);
                  }
                }
              } catch (e) {
                // Drug not found, leave empty
              }
            }
          });
        }
      } catch (e) {
        console.error('Failed to parse duplicate items:', e);
      }
    }
  }, [duplicateItemsParam, setValue]);

  // Patient search (disabled when patient is already selected to avoid showing dropdown)
  const selectedPatientId = watch('patientId');
  const { data: patientResults, isFetching: patientSearching } = useQuery({
    queryKey: ['patients', 'search', patientSearch],
    queryFn: () => patientsService.search({ q: patientSearch }),
    enabled: patientSearch.length >= 2 && !selectedPatientId,
  });

  // Drug search - use the active search index
  const activeDrugQuery = activeDrugSearchIndex !== null ? drugSearches[activeDrugSearchIndex] || '' : '';

  const { data: drugResults, isFetching: drugSearching } = useQuery({
    queryKey: ['drugs', 'search', activeDrugQuery],
    queryFn: () => drugsService.search({ q: activeDrugQuery }),
    enabled: activeDrugQuery.length >= 2,
  });

  // Get list of selected drug IDs for interaction checking (useWatch triggers re-renders)
  const watchedItems = useWatch({ control, name: 'items' }) || [];
  const drugIds = watchedItems
    .map((item) => item.drugId)
    .filter((id): id is string => Boolean(id));

  // Create a stable key for the query
  const drugIdsKey = [...drugIds].sort().join(',');

  // Check for drug interactions when 2+ drugs are selected
  const { data: interactionsData, isLoading: isCheckingInteractions } = useQuery({
    queryKey: ['drug-interactions', drugIdsKey],
    queryFn: () => drugsService.checkInteractions(drugIds),
    enabled: drugIds.length >= 2,
    staleTime: 30000, // Cache for 30 seconds
  });

  const interactions: InteractionWarning[] = interactionsData?.warnings || [];

  // Reset acknowledgment when interactions change
  useEffect(() => {
    setWarningsAcknowledged(false);
  }, [drugIdsKey]);

  const createMutation = useMutation({
    mutationFn: prescriptionsService.create,
    onSuccess: (data) => {
      navigate(`/doctor/prescriptions/${data.id}`);
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const onSubmit = (data: PrescriptionForm) => {
    setError(null);

    // Require acknowledgment if there are interactions
    if (interactions.length > 0 && !warningsAcknowledged) {
      setError(t('interactions.mustAcknowledge'));
      return;
    }

    const items = data.items.map((raw) => {
      const { drugName, controlledClass, noticeUrl, packagingSize, dosageFormRaw, ...item } = raw;
      if (isMedication) {
        return {
          drugId: item.drugId,
          dosage: item.dosage,
          frequency: item.frequency,
          duration: item.duration || undefined,
          quantity: item.quantity || undefined,
          quantityUnit: item.quantityUnit,
          instructions: item.instructions || undefined,
          substitutionAllowed: item.substitutionAllowed,
        };
      }
      return {
        itemLabel: item.itemLabel,
        instructions: item.instructions || undefined,
        substitutionAllowed: false,
      };
    });
    createMutation.mutate({
      patientId: data.patientId,
      documentType: data.documentType,
      prescriptionType: data.prescriptionType,
      diagnosis: data.diagnosis || undefined,
      notes: data.notes || undefined,
      items,
      validityDays: data.validityDays || undefined,
      acknowledgeWarnings: warningsAcknowledged,
    });
  };

  // Auto-fill defaults based on drug dosage form
  const getDefaultsForDrug = (drug: { dosageForm: string; strength: string; packagingSize?: number }) => {
    const form = drug.dosageForm.toLowerCase();

    // Determine default dosage based on form
    let dosage = '1 unité';
    let frequency = '3 fois par jour';
    let duration = '7 jours';
    let quantity = 21;
    let quantityUnit = 'unité';

    if (form.includes('comp') || form.includes('tablet') || form.includes('gél') || form.includes('capsul')) {
      dosage = '1 comprimé';
      frequency = '3 fois par jour';
      duration = '7 jours';
      quantity = 21;
      quantityUnit = 'comprimé';
    } else if (form.includes('sirop') || form.includes('solution') || form.includes('susp')) {
      dosage = '5 ml';
      frequency = '3 fois par jour';
      duration = '7 jours';
      quantity = 1;
      quantityUnit = 'flacon';
    } else if (form.includes('inject') || form.includes('amp')) {
      dosage = '1 ampoule';
      frequency = '1 fois par jour';
      duration = '5 jours';
      quantity = 5;
      quantityUnit = 'ampoule';
    } else if (form.includes('aéro') || form.includes('inhal') || form.includes('spray')) {
      dosage = '2 bouffées';
      frequency = 'si besoin';
      duration = '30 jours';
      quantity = 1;
      quantityUnit = 'flacon';
    } else if (form.includes('pommade') || form.includes('crème') || form.includes('gel') || form.includes('topique')) {
      dosage = '1 application';
      frequency = '2 fois par jour';
      duration = '14 jours';
      quantity = 1;
      quantityUnit = 'tube';
    } else if (form.includes('suppo')) {
      dosage = '1 suppositoire';
      frequency = '2 fois par jour';
      duration = '5 jours';
      quantity = 10;
      quantityUnit = 'suppositoire';
    } else if (form.includes('goutte') || form.includes('collyre')) {
      dosage = '1 goutte';
      frequency = '3 fois par jour';
      duration = '7 jours';
      quantity = 1;
      quantityUnit = 'flacon';
    } else if (form.includes('patch')) {
      dosage = '1 patch';
      frequency = 'tous les 3 jours';
      duration = '30 jours';
      quantity = 10;
      quantityUnit = 'patch';
    } else if (form.includes('sachet') || form.includes('poudre')) {
      dosage = '1 sachet';
      frequency = '3 fois par jour';
      duration = '7 jours';
      quantity = 21;
      quantityUnit = 'sachet';
    }

    // Round up to nearest package size
    if (drug.packagingSize && drug.packagingSize > 1 && quantity > 1) {
      quantity = Math.ceil(quantity / drug.packagingSize) * drug.packagingSize;
    }

    return { dosage, frequency, duration, quantity, quantityUnit };
  };

  const blankMedicationItem = () => ({ drugId: '', dosage: '', frequency: '', duration: '', quantity: 1, quantityUnit: 'unité', substitutionAllowed: true });
  const blankOrderItem = () => ({ itemLabel: '', instructions: '', substitutionAllowed: false });

  const choiceCard = (selected: boolean) =>
    `flex items-center justify-center gap-2 text-center p-3 rounded-lg border-2 cursor-pointer transition-colors text-sm font-medium has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-1 ${
      selected
        ? 'border-primary-500 bg-primary-50 text-primary-700'
        : 'border-gray-200 text-gray-700 hover:border-gray-300'
    }`;

  const onInvalid = () => setError(t('doctor.rx.formInvalid'));

  const patientQueryActive = patientSearch.length >= 2 && !selectedPatientId;
  const patients = patientResults?.patients ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={isMedication ? t('prescription.new') : t('prescription.newDocument')} />

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {/* Document Type */}
        <div className="card">
          <h2 className="card-title mb-4">{t('prescription.documentType')}</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(['MEDICATION', 'IMAGING', 'LAB', 'REFERRAL'] as const).map((type) => (
              <label key={type} className={choiceCard(documentType === type)}>
                <input
                  type="radio"
                  value={type}
                  className="sr-only"
                  {...register('documentType')}
                  onChange={() => {
                    setValue('documentType', type);
                    // Reset items to a single blank line of the appropriate shape
                    setDrugSearches({});
                    setActiveDrugSearchIndex(null);
                    if (type === 'MEDICATION') {
                      setValue('items', [blankMedicationItem()]);
                    } else {
                      setValue('items', [blankOrderItem()]);
                    }
                  }}
                />
                {t(`prescription.docTypes.${type.toLowerCase()}`)}
              </label>
            ))}
          </div>
        </div>

        {/* Patient Selection */}
        <div className="card">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="card-title">
              <label htmlFor="rx-patient-search">{t('prescription.patient')}</label>
            </h2>
            <button
              type="button"
              onClick={() => navigate('/doctor/patients/new')}
              className="btn-ghost btn-sm text-primary-700"
            >
              <UserPlus className="h-4 w-4" />
              {t('patient.new')}
            </button>
          </div>

          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="rx-patient-search"
                  type="text"
                  autoComplete="off"
                  placeholder={t('prescription.selectPatient')}
                  className={`${errors.patientId ? 'input-error' : selectedPatientId ? 'input-success' : 'input'} ps-9 pe-9`}
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    if (selectedPatientId) {
                      setValue('patientId', '');
                    }
                  }}
                />
                {patientQueryActive && patientSearching && (
                  <Spinner className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                )}
                {selectedPatientId && (
                  <Check className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-success-600" />
                )}
              </div>
              {selectedPatientId && (
                <button
                  type="button"
                  onClick={() => {
                    setValue('patientId', '');
                    setPatientSearch('');
                  }}
                  className="btn-secondary px-3"
                  title={t('doctor.rx.changePatient')}
                  aria-label={t('doctor.rx.changePatient')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {patientQueryActive && patientResults && (
              <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {patients.length > 0 ? (
                  patients.map((patient) => {
                    const pid = patientIdentifier(patient);
                    return (
                      <button
                        key={patient.id}
                        type="button"
                        className="w-full border-b border-gray-100 px-4 py-3 text-start last:border-0 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                        onClick={() => {
                          setValue('patientId', patient.id, { shouldValidate: true });
                          setPatientSearch(`${patient.lastName} ${patient.firstName} (${pid.masked})`);
                        }}
                      >
                        <p className="truncate font-medium text-gray-900">
                          {patient.lastName} {patient.firstName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {t(pid.typeKey)}: {pid.masked} · {patient.birthYear} · {t(`patient.${patient.gender.toLowerCase()}`)}
                        </p>
                      </button>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-gray-500">
                    <span>{t('doctor.rx.noPatientFound')}</span>
                    <button
                      type="button"
                      className="link shrink-0"
                      onClick={() => navigate('/doctor/patients/new')}
                    >
                      {t('patient.new')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <input type="hidden" {...register('patientId', { required: true })} />
          {errors.patientId && <p className="field-error">{t('doctor.rx.patientRequired')}</p>}
        </div>

        {/* Medications / Orders */}
        <div className="card">
          <h2 className="card-title mb-4">{isMedication ? t('prescription.medications') : t('prescription.orders')}</h2>

          <div className="space-y-4">
            {fields.map((field, index) => {
              const itemErrors = errors.items?.[index];
              const drugName = watch(`items.${index}.drugName`);
              const controlledClass = watch(`items.${index}.controlledClass`);
              const drugQuery = drugSearches[index] || '';
              return (
              <div key={field.id} className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-2 text-xs font-semibold text-gray-600 ring-1 ring-gray-200">
                    {index + 1}
                  </span>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        remove(index);
                        // Search inputs are keyed by index; reset them so they don't shift onto other rows
                        setDrugSearches({});
                        setActiveDrugSearchIndex(null);
                      }}
                      className="btn-ghost btn-sm text-danger-700 hover:bg-danger-50 hover:text-danger-800"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t('common.delete')}
                    </button>
                  )}
                </div>

                {isMedication && (
                <div className="grid gap-3">
                  {/* Drug search */}
                  <div className="relative">
                    <label htmlFor={`rx-drug-${index}`} className="label">{t('prescription.drug')}</label>
                    <div className="relative">
                      <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        id={`rx-drug-${index}`}
                        type="text"
                        autoComplete="off"
                        className={`${itemErrors?.drugId && !drugName ? 'input-error' : 'input'} ps-9 pe-9`}
                        placeholder={t('doctor.rx.searchDrugPlaceholder')}
                        value={drugQuery}
                        onFocus={() => setActiveDrugSearchIndex(index)}
                        onChange={(e) => {
                          setActiveDrugSearchIndex(index);
                          setDrugSearches({ ...drugSearches, [index]: e.target.value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setActiveDrugSearchIndex(null);
                        }}
                      />
                      {activeDrugSearchIndex === index && drugQuery.length >= 2 && drugSearching && (
                        <Spinner className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                      )}
                    </div>

                    {activeDrugSearchIndex === index && drugQuery.length >= 2 && drugResults?.drugs && (
                      <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {drugResults.drugs.length === 0 && (
                          <p className="px-4 py-3 text-sm text-gray-500">{t('doctor.rx.noDrugFound')}</p>
                        )}
                        {drugResults.drugs.map((drug) => (
                          <div key={drug.id} className="flex items-start border-b border-gray-100 last:border-0 hover:bg-gray-50">
                            <button
                              type="button"
                              className="min-w-0 flex-1 px-4 py-2 text-start focus:bg-gray-50 focus:outline-none"
                              onClick={() => {
                                setValue(`items.${index}.drugId`, drug.id, { shouldValidate: true });
                                setDrugSearches({ ...drugSearches, [index]: '' });
                                setActiveDrugSearchIndex(null);
                                setValue(`items.${index}.drugName`, `${drug.tradeName} (${drug.dci}) - ${drug.strength}`);
                                setValue(`items.${index}.controlledClass`, drug.controlledClass ?? '');
                                setValue(`items.${index}.noticeUrl`, drug.noticeUrl ?? '');

                                // Auto-disable substitution for Tableau A (stupéfiants)
                                if (drug.controlledClass === 'A') {
                                  setValue(`items.${index}.substitutionAllowed`, false);
                                }

                                // Save packaging fields for hint display
                                setValue(`items.${index}.packagingSize`, drug.packagingSize ?? 0);
                                setValue(`items.${index}.dosageFormRaw`, drug.dosageForm);

                                // Auto-fill default values based on dosage form
                                const defaults = getDefaultsForDrug({ ...drug, packagingSize: drug.packagingSize });
                                setValue(`items.${index}.dosage`, defaults.dosage);
                                setValue(`items.${index}.frequency`, defaults.frequency);
                                setValue(`items.${index}.duration`, defaults.duration);
                                setValue(`items.${index}.quantity`, defaults.quantity);
                                setValue(`items.${index}.quantityUnit`, defaults.quantityUnit);
                              }}
                            >
                              <p className="flex flex-wrap items-center gap-2 font-medium text-gray-900">
                                <span className="break-words">{drug.tradeName}</span>
                                {drug.controlledClass && (
                                  <span className="badge-warning">{t('doctor.rx.tableau', { cls: drug.controlledClass })}</span>
                                )}
                              </p>
                              <p className="break-words text-sm text-gray-600">
                                {drug.dci} - {drug.strength} - {drug.dosageForm}
                              </p>
                              {drug.packagingDescription && (
                                <p className="text-xs text-gray-500">{drug.packagingDescription}</p>
                              )}
                            </button>
                            <div className="flex shrink-0 items-center gap-0.5 py-1.5 pe-2">
                              <button
                                type="button"
                                className="btn-icon h-8 w-8 text-primary-600 hover:text-primary-700"
                                title={t('doctor.rx.viewNotice')}
                                aria-label={t('doctor.rx.viewNotice')}
                                onClick={() => setNoticeModalDrugId(drug.id)}
                              >
                                <FileText className="h-4 w-4" />
                              </button>
                              <a
                                href={`https://www.google.com/search?q=${encodeURIComponent(drug.tradeName + ' ' + drug.strength + ' boite')}&tbm=isch`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-icon h-8 w-8 text-gray-400"
                                title={t('doctor.rx.viewPackaging')}
                                aria-label={t('doctor.rx.viewPackaging')}
                              >
                                <ImageIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {drugName && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm font-medium text-primary-700">
                        <Check className="h-4 w-4 shrink-0" />
                        <span className="break-words">{drugName}</span>
                        <button
                          type="button"
                          className="btn-icon h-7 w-7 text-primary-600 hover:text-primary-700"
                          title={t('doctor.rx.viewNotice')}
                          aria-label={t('doctor.rx.viewNotice')}
                          onClick={() => setNoticeModalDrugId(watch(`items.${index}.drugId`) || null)}
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                        {controlledClass && (
                          <span className="badge-warning">{t('doctor.rx.tableau', { cls: controlledClass })}</span>
                        )}
                      </div>
                    )}
                    <input type="hidden" {...register(`items.${index}.drugId`, { required: true })} />
                    {itemErrors?.drugId && !drugName && <p className="field-error">{t('doctor.rx.drugRequired')}</p>}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`rx-dosage-${index}`} className="label">{t('prescription.dosage')}</label>
                      <input
                        id={`rx-dosage-${index}`}
                        type="text"
                        className={itemErrors?.dosage ? 'input-error' : 'input'}
                        placeholder="1 comprimé"
                        {...register(`items.${index}.dosage`, { required: true })}
                      />
                      {itemErrors?.dosage && <p className="field-error">{t('common.required')}</p>}
                    </div>
                    <div>
                      <label htmlFor={`rx-frequency-${index}`} className="label">{t('prescription.frequency')}</label>
                      <input
                        id={`rx-frequency-${index}`}
                        type="text"
                        className={itemErrors?.frequency ? 'input-error' : 'input'}
                        placeholder="3 fois par jour"
                        {...register(`items.${index}.frequency`, { required: true })}
                      />
                      {itemErrors?.frequency && <p className="field-error">{t('common.required')}</p>}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`rx-duration-${index}`} className="label">
                        {t('prescription.duration')} <span className="font-normal text-gray-400">({t('common.optional')})</span>
                      </label>
                      <input
                        id={`rx-duration-${index}`}
                        type="text"
                        className="input"
                        placeholder="7 jours"
                        {...register(`items.${index}.duration`)}
                      />
                    </div>
                    <div>
                      <label htmlFor={`rx-quantity-${index}`} className="label">
                        {t('prescription.quantity')} <span className="font-normal text-gray-400">({t('common.optional')})</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id={`rx-quantity-${index}`}
                          type="number"
                          min="1"
                          className="input w-24 shrink-0"
                          {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                        />
                        <select
                          className="input min-w-0 flex-1"
                          aria-label={t('prescription.quantity')}
                          {...register(`items.${index}.quantityUnit`)}
                        >
                          {QUANTITY_UNITS.map(([value, key]) => (
                            <option key={value} value={value}>{t(`prescription.units.${key}`)}</option>
                          ))}
                        </select>
                      </div>
                      {(() => {
                        const hint = formatPackagingHint(
                          watch(`items.${index}.quantity`) || 0,
                          watch(`items.${index}.packagingSize`),
                          watch(`items.${index}.dosageFormRaw`)
                        );
                        return hint ? <p className="field-hint">{hint}</p> : null;
                      })()}
                    </div>
                  </div>

                  <div>
                    <label htmlFor={`rx-instructions-${index}`} className="label">{t('prescription.instructions')}</label>
                    <input
                      id={`rx-instructions-${index}`}
                      type="text"
                      className="input"
                      placeholder={t('common.optional')}
                      {...register(`items.${index}.instructions`)}
                    />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {INSTRUCTION_CHIPS.map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => {
                            const current = watch(`items.${index}.instructions`) || '';
                            const newVal = current ? `${current}, ${chip.toLowerCase()}` : chip;
                            setValue(`items.${index}.instructions`, newVal);
                          }}
                        >
                          <Plus className="h-3 w-3" />
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      {...register(`items.${index}.substitutionAllowed`)}
                      disabled={controlledClass === 'A'}
                      className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-gray-700">
                      {t('prescription.substitutionAllowed')}
                      {controlledClass === 'A' && (
                        <span className="ms-1 text-xs text-danger-600">
                          ({t('controlled.substitutionDisabled')})
                        </span>
                      )}
                    </span>
                  </label>
                </div>
                )}

                {!isMedication && (
                <div className="grid gap-3">
                  <div>
                    <label htmlFor={`rx-label-${index}`} className="label">{t('prescription.orderLabel')} *</label>
                    <input
                      id={`rx-label-${index}`}
                      type="text"
                      className={itemErrors?.itemLabel ? 'input-error' : 'input'}
                      placeholder={t(`prescription.orderLabelPlaceholders.${documentType.toLowerCase()}`, '')}
                      {...register(`items.${index}.itemLabel`, { required: true })}
                    />
                    {itemErrors?.itemLabel && <p className="field-error">{t('common.required')}</p>}
                  </div>
                  <div>
                    <label htmlFor={`rx-details-${index}`} className="label">
                      {t('prescription.orderDetails')}{' '}
                      <span className="font-normal text-gray-400">({t('common.optional')})</span>
                    </label>
                    <textarea
                      id={`rx-details-${index}`}
                      rows={2}
                      className="input"
                      {...register(`items.${index}.instructions`)}
                    />
                  </div>
                </div>
                )}
              </div>
              );
            })}

            <button
              type="button"
              onClick={() => append(isMedication ? blankMedicationItem() : blankOrderItem())}
              className="btn-outline w-full border-dashed"
            >
              <Plus className="h-4 w-4" />
              {isMedication ? t('prescription.addMedication') : t('prescription.addOrder')}
            </button>
          </div>
        </div>

        {/* Controlled Substance Warnings */}
        {watchedItems.some((item) => item.controlledClass) && (
          <div className="card border-warning-200 bg-warning-50">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-warning-800">
              <ShieldAlert className="h-5 w-5" />
              {t('controlled.warning')}
            </h3>
            <div className="space-y-2">
              {watchedItems
                .filter((item) => item.controlledClass)
                .map((item, i) => (
                  <div key={i} className={`rounded-lg border bg-white p-3 ${item.controlledClass === 'A' ? 'border-danger-200' : 'border-warning-200'}`}>
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                      <span className="break-words">{item.drugName}</span>
                      <span className={item.controlledClass === 'A' ? 'badge-danger' : 'badge-warning'}>
                        {t(`controlled.tableau${item.controlledClass}`)}
                      </span>
                    </p>
                    <p className="mt-1 text-sm text-gray-700">
                      {item.controlledClass === 'A' && t('controlled.tableauADoctor')}
                      {item.controlledClass === 'B' && t('controlled.tableauBDoctor')}
                      {item.controlledClass === 'C' && t('controlled.tableauCDoctor')}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Drug Interaction Warnings */}
        {isCheckingInteractions && drugIds.length >= 2 && (
          <div className="card flex items-center gap-3 p-4 text-sm text-gray-600">
            <Spinner className="h-4 w-4" />
            <span>{t('common.loading')}</span>
          </div>
        )}

        {interactions.length > 0 && (
          <div className="card border-danger-200 bg-danger-50">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-danger-800">
              <AlertTriangle className="h-5 w-5" />
              {t('interactions.detected')}
            </h3>
            <div className="space-y-3">
              {interactions.map((interaction, i) => (
                <div
                  key={i}
                  className={`rounded-lg border p-3 ${severityStyles[interaction.severity] ?? severityStyles.MILD}`}
                >
                  <div className="mb-1 flex items-start justify-between gap-3">
                    <p className="break-words font-medium">
                      {interaction.drug1.tradeName} + {interaction.drug2.tradeName}
                    </p>
                    <span className="shrink-0 rounded bg-white/60 px-2 py-0.5 text-xs font-semibold">
                      {t(`interactions.${interaction.severity.toLowerCase()}`)}
                    </span>
                  </div>
                  <p className="text-sm">{interaction.description}</p>
                  <p className="mt-1 text-sm italic">
                    <strong>{t('interactions.recommendation')}:</strong> {interaction.recommendation}
                  </p>
                </div>
              ))}
            </div>
            <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-lg border border-danger-200 bg-white p-3 hover:bg-danger-50">
              <input
                type="checkbox"
                checked={warningsAcknowledged}
                onChange={(e) => setWarningsAcknowledged(e.target.checked)}
                className="h-5 w-5 shrink-0 rounded border-danger-200 text-danger-600 focus:ring-danger-500"
              />
              <span className="font-medium text-danger-800">
                {t('interactions.acknowledge')}
              </span>
            </label>
          </div>
        )}

        {/* Additional info */}
        <div className="card">
          <div className="grid gap-4">
            <div>
              <label htmlFor="rx-diagnosis" className="label">{t('prescription.diagnosis')}</label>
              <textarea
                id="rx-diagnosis"
                className="input"
                rows={2}
                {...register('diagnosis')}
              />
            </div>

            <div>
              <label htmlFor="rx-notes" className="label">{t('prescription.notes')}</label>
              <textarea
                id="rx-notes"
                className="input"
                rows={2}
                {...register('notes')}
              />
            </div>

            {isMedication && (
            <fieldset>
              <legend className="label">{t('prescription.type')}</legend>
              <div className="grid grid-cols-2 gap-3">
                <label className={choiceCard(watch('prescriptionType') === 'ACUTE')}>
                  <input
                    type="radio"
                    value="ACUTE"
                    className="sr-only"
                    {...register('prescriptionType')}
                    onChange={() => {
                      setValue('prescriptionType', 'ACUTE');
                      setValue('validityDays', 15);
                    }}
                  />
                  <span>{t('prescription.acute')}</span>
                  <span className="text-xs font-normal opacity-75">({t('doctor.rx.daysShort', { n: 15 })})</span>
                </label>
                <label className={choiceCard(watch('prescriptionType') === 'CHRONIC')}>
                  <input
                    type="radio"
                    value="CHRONIC"
                    className="sr-only"
                    {...register('prescriptionType')}
                    onChange={() => {
                      setValue('prescriptionType', 'CHRONIC');
                      setValue('validityDays', 30);
                    }}
                  />
                  <span>{t('prescription.chronic')}</span>
                  <span className="text-xs font-normal opacity-75">({t('doctor.rx.daysShort', { n: 30 })})</span>
                </label>
              </div>
            </fieldset>
            )}

            <div>
              <label htmlFor="rx-validity" className="label">
                {t('prescription.validity')} ({t('prescription.days')})
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  id="rx-validity"
                  type="number"
                  min="1"
                  max="365"
                  className="input w-24"
                  {...register('validityDays', { valueAsNumber: true })}
                />
                <div className="flex flex-wrap gap-1.5">
                  {[15, 30, 90, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={watch('validityDays') === d}
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${watch('validityDays') === d ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setValue('validityDays', d)}
                    >
                      {t('doctor.rx.daysShort', { n: d })}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert-danger" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}

        {/* Submit */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary sm:min-w-32"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="btn-primary sm:min-w-32"
          >
            {createMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>

      <DrugNoticeModal
        drugId={noticeModalDrugId}
        isOpen={!!noticeModalDrugId}
        onClose={() => setNoticeModalDrugId(null)}
      />
    </div>
  );
}
